import {
  computed,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  NgZone,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { doc, Firestore, onSnapshot, setDoc, Unsubscribe } from '@angular/fire/firestore';

import { toAppError } from '../../../shared/utils/app-error.util';
import { toFavoritesPayload } from '../../../shared/utils/favorites-payload.util';
import { FIRESTORE_PATHS } from '../../config/firestore-paths';
import type { AppError } from '../../interfaces/app-error.interface';
import type {
  FavoriteFolder,
  FavoriteItem,
  UserFavoritesDoc,
} from '../../interfaces/layout.interface';
import type { ToolEntry } from '../../interfaces/tool.interface';
import { AuthService } from '../auth/auth.service';
import { FirebaseAuthSyncService } from '../auth/firebase-auth-sync.service';
import { translateNow } from '../i18n/translate-now';
import { ToolRegistryService } from './tool-registry.service';

@Injectable({ providedIn: 'root' })
export class FavoritesService {
  private readonly firestore = inject(Firestore);
  private readonly authService = inject(AuthService);
  private readonly firebaseAuthSync = inject(FirebaseAuthSyncService);
  private readonly toolRegistry = inject(ToolRegistryService);
  private readonly ngZone = inject(NgZone);
  private readonly environmentInjector = inject(EnvironmentInjector);

  private readonly _doc = signal<UserFavoritesDoc | null>(null);
  private unsubscribe: Unsubscribe | null = null;

  /** Non-null when the Firestore listener fails */
  readonly loadError = signal<AppError | null>(null);
  /** True while a {@link retry} is waiting for the listener to answer. */
  readonly retrying = signal(false);
  private currentUserId: string | null = null;

  /** True once the first snapshot (or a load error) has settled — before that, `items()`/
   *  `folders()` read as empty for lack of data, not because the user has no favorites. */
  readonly loaded = computed(() => this._doc() !== null || this.loadError() !== null);

  readonly folders = computed(() => this._doc()?.folders ?? []);
  readonly items = computed(() => this._doc()?.items ?? []);

  /**
   * Enriched favorite items with tool information from the registry
   */
  readonly favoriteTools = computed(() => {
    const allTools = this.toolRegistry.allToolsById();
    return this.items()
      .map((item) => {
        const tool = allTools.get(item.toolCode);
        if (!tool) return null;
        return { ...item, tool };
      })
      .filter(
        (
          item,
        ): item is {
          id: string;
          toolCode: string;
          folderId: string | null;
          order: number;
          tool: ToolEntry;
        } => item !== null,
      )
      .sort((a, b) => a.order - b.order);
  });

  /**
   * Legacy computed for backward compatibility with SidebarComponent
   * Returns just the tool codes of all favorite items
   */
  readonly allFavoriteIds = computed(() => this.items().map((item) => item.toolCode));

  constructor() {
    effect((onCleanup) => {
      const userId = this.authService.user()?.sub;
      // Also track Firebase Auth's own state (not just Auth0's): the Auth0→Firebase
      // custom-token exchange can still be in flight when userId first resolves, so a
      // listener started right then can hit a permission-denied that never recovers on its
      // own. Re-running on every Firebase auth transition closes that race.
      this.firebaseAuthSync.firebaseUser();
      this.currentUserId = userId ?? null;
      if (userId) {
        this.listenFavorites(userId);
      } else {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this._doc.set(null);
      }
      onCleanup(() => {
        this.unsubscribe?.();
        this.unsubscribe = null;
      });
    });
  }

  /** Re-subscribes the favorites listener after a failure (Firestore stops a listener after an error). */
  retry(): void {
    if (!this.currentUserId) return;
    this.retrying.set(true);
    this.loadError.set(null);
    this.listenFavorites(this.currentUserId);
  }

  /**
   * Listen to favorites document in Firestore and update the signal
   * Path: fury-ui/config/favorites/{userId}
   * @private
   */
  private listenFavorites(userId: string): void {
    this.unsubscribe?.();

    this.unsubscribe = onSnapshot(
      doc(this.firestore, FIRESTORE_PATHS.FAVORITES, userId),
      (snapshot) => {
        // Firestore's listener runs outside Angular's zone and injection context — see
        // FirebaseAuthSyncService.start() for the full rationale on why this throws
        // NG0203/NG0200 in production builds without the explicit wrap.
        this.ngZone.run(() => {
          runInInjectionContext(this.environmentInjector, () => {
            this.loadError.set(null);
            this.retrying.set(false);
            if (snapshot.exists()) {
              const data = snapshot.data() as Omit<UserFavoritesDoc, 'id' | 'userId'>;
              this._doc.set({
                id: snapshot.id,
                userId,
                folders: data.folders ?? [],
                items: data.items ?? [],
                created_at: data.created_at,
                updated_at: data.updated_at,
              });
            } else {
              this._doc.set({ id: userId, userId, folders: [], items: [] });
            }
          });
        });
      },
      (error) => {
        console.error('[FavoritesService] listener error:', error);
        this.ngZone.run(() => {
          this.loadError.set(toAppError(error));
          this.retrying.set(false);
        });
      },
    );
  }

  /**
   * Check if a tool is favorited
   */
  isFavorite(toolCode: string): boolean {
    return this.items().some((item) => item.toolCode === toolCode);
  }

  /** Resolves the current user + their favorites doc, or throws if either isn't ready yet. */
  private requireContext(): { userId: string; doc: UserFavoritesDoc } {
    const userId = this.authService.user()?.sub;
    if (!userId) {
      throw new Error(translateNow('errors.noSession'));
    }

    const currentDoc = this._doc();
    if (!currentDoc) {
      throw new Error(translateNow('errors.favoritesNotReady'));
    }

    return { userId, doc: currentDoc };
  }

  /**
   * Toggle favorite status of a tool
   */
  async toggleFavorite(toolCode: string): Promise<void> {
    const { userId, doc: currentDoc } = this.requireContext();

    const isFav = this.isFavorite(toolCode);
    const updatedItems = isFav
      ? currentDoc.items.filter((item) => item.toolCode !== toolCode)
      : [
          ...currentDoc.items,
          {
            id: `${toolCode}_${Date.now()}`,
            toolCode,
            folderId: null,
            order: currentDoc.items.length,
          } as FavoriteItem,
        ];

    await this.updateFavoritesDoc(userId, {
      ...currentDoc,
      items: updatedItems,
      updated_at: new Date(),
    });
  }

  /**
   * Add a new folder
   */
  async addFolder(name: string, color: { name: string; hex: string }): Promise<string> {
    const { userId, doc: currentDoc } = this.requireContext();

    const folderId = 'folder_' + Date.now();
    const newFolder: FavoriteFolder = {
      id: folderId,
      name,
      color,
      open: true,
      order: currentDoc.folders.length,
    };

    const updatedDoc: UserFavoritesDoc = {
      ...currentDoc,
      folders: [...currentDoc.folders, newFolder],
      updated_at: new Date(),
    };

    await this.updateFavoritesDoc(userId, updatedDoc);
    return folderId;
  }

  /**
   * Update folder properties
   */
  async updateFolder(folderId: string, changes: Partial<FavoriteFolder>): Promise<void> {
    const { userId, doc: currentDoc } = this.requireContext();

    const updatedFolders = currentDoc.folders.map((folder) =>
      folder.id === folderId ? { ...folder, ...changes } : folder,
    );

    const updatedDoc: UserFavoritesDoc = {
      ...currentDoc,
      folders: updatedFolders,
      updated_at: new Date(),
    };

    await this.updateFavoritesDoc(userId, updatedDoc);
  }

  /**
   * Delete a folder and move its items to uncategorized (folderId: null)
   */
  async deleteFolder(folderId: string): Promise<void> {
    const { userId, doc: currentDoc } = this.requireContext();

    const updatedFolders = currentDoc.folders.filter((folder) => folder.id !== folderId);
    const updatedItems = currentDoc.items.map((item) =>
      item.folderId === folderId ? { ...item, folderId: null } : item,
    );

    const updatedDoc: UserFavoritesDoc = {
      ...currentDoc,
      folders: updatedFolders,
      items: updatedItems,
      updated_at: new Date(),
    };

    await this.updateFavoritesDoc(userId, updatedDoc);
  }

  /**
   * Move a favorite item to a folder (or remove from folder if folderId is null)
   */
  async moveToFolder(itemId: string, folderId: string | null): Promise<void> {
    const { userId, doc: currentDoc } = this.requireContext();

    const updatedItems = currentDoc.items.map((item) =>
      item.id === itemId ? { ...item, folderId } : item,
    );

    const updatedDoc: UserFavoritesDoc = {
      ...currentDoc,
      items: updatedItems,
      updated_at: new Date(),
    };

    await this.updateFavoritesDoc(userId, updatedDoc);
  }

  /**
   * Reorder favorite items
   */
  async reorderItems(items: FavoriteItem[]): Promise<void> {
    const { userId, doc: currentDoc } = this.requireContext();

    const reorderedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    const updatedDoc: UserFavoritesDoc = {
      ...currentDoc,
      items: reorderedItems,
      updated_at: new Date(),
    };

    await this.updateFavoritesDoc(userId, updatedDoc);
  }

  /**
   * Update the favorites document in Firestore with merge strategy
   * @private
   */
  private async updateFavoritesDoc(userId: string, favDoc: UserFavoritesDoc): Promise<void> {
    await setDoc(doc(this.firestore, FIRESTORE_PATHS.FAVORITES, userId), toFavoritesPayload(favDoc), { merge: true });
  }
}
